import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Button } from '@magnetic/button'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Skeleton } from '@magnetic/skeleton'
import { Tag } from '@magnetic/tag'
import {
  Database,
  Package,
  HardDrives,
  Warning,
  ArrowsClockwise,
} from '@/lib/icons'
import { getRegistryOverview } from '@/lib/api'
import { bigIntToNumber } from '@/lib/protobuf-utils'

function formatBytes(bytes: number | bigint | undefined): string {
  if (!bytes) return '0 B'
  const b = typeof bytes === 'bigint' ? Number(bytes) : bytes
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatNumber(n: number | bigint | undefined): string {
  if (!n) return '0'
  const num = typeof n === 'bigint' ? Number(n) : n
  return num.toLocaleString()
}

export function ECROverview() {
  const { data: statsResponse, isLoading, error, refetch } = useConnectQuery(
    getRegistryOverview,
    {},
    {
      refetchInterval: 60000,
    }
  )

  const stats = statsResponse?.stats

  if (isLoading) {
    return (
      <Flex gap="md" wrap>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} style={{ flex: '1 1 200px', minWidth: 200 }}>
            <Flex direction="vertical" gap="sm" style={{ padding: 16 }}>
              <Flex align="center" justify="space-between">
                <Skeleton height="sm" shade="dark" style={{ width: 120 }} />
                <Skeleton height={16} style={{ width: 16 }} />
              </Flex>
              <Skeleton height={32} style={{ width: 60 }} />
              <Skeleton height="sm" style={{ width: 100 }} />
            </Flex>
          </Card>
        ))}
      </Flex>
    )
  }

  if (error) {
    return (
      <Card>
        <Flex direction="vertical" align="center" gap="md" style={{ padding: 24 }}>
          <Warning size={32} style={{ color: 'var(--mds-color-theme-text-error-normal)' }} />
          <Text size="sm" color="light">
            {error instanceof Error ? error.message : 'Failed to load registry statistics'}
          </Text>
          <Button kind="secondary" size="sm" onClick={() => refetch()} icon={<ArrowsClockwise size={16} />}>
            Retry
          </Button>
        </Flex>
      </Card>
    )
  }

  return (
    <Flex gap="md" wrap>
      <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
        <Flex direction="vertical" gap="sm" style={{ padding: 16 }}>
          <Flex align="center" justify="space-between">
            <Text size="sm" weight="semi-bold">Total Repositories</Text>
            <Database size={16} style={{ opacity: 0.5 }} />
          </Flex>
          <Heading size="primary">{stats?.totalRepositories || 0}</Heading>
          <Text size="xs" color="light">ECR repositories</Text>
        </Flex>
      </Card>

      <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
        <Flex direction="vertical" gap="sm" style={{ padding: 16 }}>
          <Flex align="center" justify="space-between">
            <Text size="sm" weight="semi-bold">Total Images</Text>
            <Package size={16} style={{ opacity: 0.5 }} />
          </Flex>
          <Heading size="primary">{formatNumber(bigIntToNumber(stats?.totalImages))}</Heading>
          <Text size="xs" color="light">Container images</Text>
        </Flex>
      </Card>

      <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
        <Flex direction="vertical" gap="sm" style={{ padding: 16 }}>
          <Flex align="center" justify="space-between">
            <Text size="sm" weight="semi-bold">Storage Used</Text>
            <HardDrives size={16} style={{ opacity: 0.5 }} />
          </Flex>
          <Heading size="primary">{formatBytes(bigIntToNumber(stats?.totalStorageBytes))}</Heading>
          <Text size="xs" color="light">Total storage</Text>
        </Flex>
      </Card>

      <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
        <Flex direction="vertical" gap="sm" style={{ padding: 16 }}>
          <Flex align="center" justify="space-between">
            <Text size="sm" weight="semi-bold">Vulnerabilities</Text>
            <Warning size={16} style={{ opacity: 0.5 }} />
          </Flex>
          <Flex align="baseline" gap="sm">
            <Heading size="primary">{stats?.imagesWithVulnerabilities || 0}</Heading>
            <Text size="sm" color="light">images</Text>
          </Flex>
          <Flex gap="sm">
            <Tag.Status status="negative">{`${stats?.criticalVulnerabilities || 0} Critical`}</Tag.Status>
            <Tag.Status status="warning">{`${stats?.highVulnerabilities || 0} High`}</Tag.Status>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  )
}
